<template>
  <div>
    <v-card>
      <v-card-title>
        The Flow Exercises Library
        <v-spacer />

        <v-btn v-if="selected.length > 0" class="mr-2 mb-2" color="secondary" @click="showBulkStatusDialog">
          MANAGE SELECTED ({{ selected.length }})
        </v-btn>

        <v-btn class="mb-2" color="primary" dark @click="showEditDialog(blankExercise)">
          CREATE
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-text-field v-model="dtSearchQuery" :append-icon="icons.mdiMagnify" label="Search" single-line hide-details dense outlined />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table ref="dataTable" v-model="selected" class="exercises-data-table" :headers="tableHeaders" :items="exercises" :search="dtSearchQuery" :expanded.sync="dtExpanded" :loading="isLoading" :items-per-page="30" :footer-props="{ 'items-per-page-options': [15, 30, 45] }" show-select single-expand show-expand item-key="id">
        <template v-slot:item.thumbnail="{ item }">
          <v-img-ex :src="item.thumbnail" max-width="80" max-height="80" />
        </template>

        <template v-slot:item.thumbnailFemale="{ item }">
          <v-img-ex :src="item.thumbnailFemale" max-width="80" max-height="80" />
        </template>

        <template v-slot:header.thumbnail>
          <span>THUMBNAIL<br>MALE</span>
        </template>

        <template v-slot:header.thumbnailFemale>
          <span>THUMBNAIL<br>FEMALE</span>
        </template>

        <template v-slot:item.creator="{ item }">
          <div
            v-if="item.creator"
            class="text-body-2 py-2"
            style="line-height: 1.4;"
            v-html="item.creator"
          ></div>

          <div v-else class="text-caption grey--text font-italic">
            System
          </div>
        </template>

        <template v-slot:item.academy="{ item }">
          <div v-if="item.academy" class="text-body-2 py-2" style="line-height: 1.4;" v-html="item.academy"></div>
          <div v-else class="text-caption grey--text font-italic">
            Global
          </div>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip v-if="item.type" small outlined color="primary" class="font-weight-medium">
            {{ item.type }}
          </v-chip>
          <span v-else class="grey--text font-italic">---</span>
        </template>

        <template v-slot:item.tags="{ item }">
          <v-chip>{{ item.tags.length }}</v-chip>
        </template>

        <template v-slot:item.searchTags="{ item }">
          <v-chip>{{ item.searchTags.length }}</v-chip>
        </template>

        <template v-slot:item.video="{ item }">
          <div class="d-flex">
            <v-icon v-if="item.video?.url && item.video.url.length" color="success">
              {{ icons.mdiGenderMale }}
            </v-icon>
            <v-icon v-if="item.videoFemale?.url && item.videoFemale.url.length" color="warning">
              {{ icons.mdiGenderFemale }}
            </v-icon>
          </div>
        </template>

        <template v-slot:item.assessmentEnabled="{ item }">
          <v-chip v-if="isAssessmentEnabled(item)" class="exercise-table-chip" color="success" dark>
            YES
          </v-chip>
        </template>

        <template v-slot:header.assessmentEnabled>
          <span>ASSESSMENT<br>ENABLED</span>
        </template>

        <template v-slot:header.presentInExercises>
          <span>PRESENT IN<br>FLOW<br>IN THE APP</span>
        </template>

        <template v-slot:item.presentInExercises="{ item }">
          <v-chip v-if="item.presentInExercises" class="exercise-table-chip" color="success" dark>
            YES
          </v-chip>
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip class="exercise-table-chip" :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon class="mr-2" @click="showEditDialog(item)">
            {{ icons.mdiPencilOutline }}
          </v-icon>
          <v-icon @click="showDeleteConfirmationDialog(item)">
            {{ icons.mdiDeleteOutline }}
          </v-icon>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <exercise-details :item="item" />
          </td>
        </template>
      </v-data-table>
    </v-card>

    <div ref="tableScrollbar" class="exercise-table-scrollbar" @scroll="onTableScrollbarScroll">
      <div class="exercise-table-scrollbar__content" :style="{ width: `${tableScrollWidth}px` }"></div>
    </div>

    <delete-dialog v-model="isDeleteDialogVisible" :item="exercise" :is-loading="isLoading" @cancel="onDeleteCanceled" @delete="onDeleteConfirmed" />
    <exercise-edit-dialog v-if="isEditDialogVisible" v-model="isEditDialogVisible" :item="exercise" :is-loading="isLoading" @cancel="onEditCanceled" @save="onEditSave" />

    <v-dialog v-model="isBulkStatusDialogVisible" max-width="720">
      <v-card>
        <v-card-title>Apply to Selected</v-card-title>
        <v-card-text class="pt-4 pb-2">
          <p>Choose an action for {{ selected.length }} selected item(s).</p>
          <v-radio-group v-model="bulkStatusValue" class="mt-4" hide-details>
            <v-radio
              v-for="option in bulkStatusOptions"
              :key="option.value"
              :value="option.value"
              class="mb-2"
            >
              <template v-slot:label>
                <span :class="option.value === 'delete-selected' ? 'error--text font-weight-medium' : ''">
                  {{ option.text }}
                </span>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="isBulkStatusDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isBulkUpdating" @click="updateBulkStatus">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <content-pack-bulk-dialogs
      :selected-items="selected"
      :content-pack-options="contentPackOptions"
      :academy-options="academyOptions"
      :selected-content-pack-id.sync="selectedContentPackId"
      :selected-academy-id.sync="selectedAcademyId"
      :is-content-pack-dialog-visible.sync="isContentPackDialogVisible"
      :is-academy-dialog-visible.sync="isAcademyDialogVisible"
      :is-content-pack-confirmation-dialog-visible.sync="isContentPackConfirmationDialogVisible"
      :is-academy-confirmation-dialog-visible.sync="isAcademyConfirmationDialogVisible"
      :is-bulk-updating="isBulkUpdating"
      :content-pack-confirmation-title="contentPackConfirmationTitle"
      :content-pack-confirmation-message="contentPackConfirmationMessage"
      :academy-dialog-title="academyDialogTitle"
      :academy-confirmation-title="academyConfirmationTitle"
      :academy-confirmation-message="academyConfirmationMessage"
      @cancel-selection="hideContentPackDialog"
      @continue-selection="showContentPackConfirmationDialog"
      @close-confirmation="resetContentPackDialogs"
      @confirm-selection="applyContentPackAction"
      @cancel-academy-selection="hideAcademyDialog"
      @continue-academy-selection="showAcademyConfirmationDialog"
      @close-academy-confirmation="resetContentPackDialogs"
      @confirm-academy-selection="applyAcademyAction"
    />

    <v-dialog v-model="isBulkDeleteDialogVisible" max-width="500">
      <v-card>
        <v-card-title>Delete Selected Items</v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete <strong>{{ selected.length }}</strong> selected item(s)?</p>
          <p class="error--text mb-0">
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="isBulkDeleteDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="isBulkDeleting" @click="deleteBulkItems">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiDeleteOutline, mdiGenderFemale, mdiGenderMale, mdiMagnify, mdiPencilOutline,
} from '@mdi/js'
import {
  onBeforeUnmount, onMounted, ref, computed, nextTick,
} from 'vue'
import ContentPackBulkDialogs from '@/components/ContentPackBulkDialogs.vue'
import DeleteDialog from '@/components/DeleteDialog.vue'
import ExerciseDetails from '@/views/exercises/partials/ExerciseDetails.vue'
import ExerciseEditDialog from '@/views/exercises/partials/ExerciseEditDialog.vue'
import VImgEx from '@/components/VImgEx.vue'
import useExercises from '@/composables/api/exercises'
import useContentPackBulkActions from '@/composables/contentPackBulkActions'
import useUniqueKey from '@/composables/uniqueKey'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAuth from '@/composables/useAuth'
import { ExerciseCategory } from '@/enums/enums'

export default {
  components: {
    ContentPackBulkDialogs, DeleteDialog, ExerciseDetails, ExerciseEditDialog, VImgEx,
  },
  setup() {
    const {
      dtHeaders,
      dtExpanded,
      dtSearchQuery,
      blankExercise,
      exercise,
      exercises,
      isLoading,
      createExercise,
      deleteExercise,
      fetchExercises,
      updateExercise,
      updateManyExercises,
    } = useExercises()
    const { uniqueKey } = useUniqueKey()
    const { showSnackbar } = useSnackbar()

    const isDeleteDialogVisible = ref(false)
    const isEditDialogVisible = ref(false)
    const selected = ref([])

    const isBulkStatusDialogVisible = ref(false)
    const bulkStatusValue = ref('active-true')
    const isBulkUpdating = ref(false)
    const {
      isContentPackDialogVisible,
      isContentPackConfirmationDialogVisible,
      isAcademyDialogVisible,
      isAcademyConfirmationDialogVisible,
      selectedContentPackId,
      selectedAcademyId,
      contentPackOptions,
      academyOptions,
      contentPackConfirmationTitle,
      contentPackConfirmationMessage,
      academyDialogTitle,
      academyConfirmationTitle,
      academyConfirmationMessage,
      hideContentPackDialog,
      hideAcademyDialog,
      showContentPackConfirmationDialog,
      showAcademyConfirmationDialog,
      beginBulkAction,
      applyContentPackAction: saveContentPackAction,
      applyAcademyAction: saveAcademyAction,
      resetContentPackDialogs,
    } = useContentPackBulkActions({
      selectedItems: selected,
      sectionKey: 'flow',
      subsectionKey: 'exercises',
      libraryType: 'FLOW_EXERCISE',
      updateManyItems: updateManyExercises,
    })
    const bulkStatusOptions = [
      { text: 'Mark as Active', value: 'active-true' },
      { text: 'Mark as Inactive', value: 'active-false' },
      { text: 'Delete Selected', value: 'delete-selected' },
      { text: 'Assign to Content Pack', value: 'content-pack-add' },
      { text: 'Remove from Content Pack', value: 'content-pack-delete' },
      { text: 'Include in Academy', value: 'academy-include' },
      { text: 'Exclude from Academy', value: 'academy-exclude' },
    ]
    const bulkStatusConfig = {
      'active-true': { paramName: 'active', paramValue: true },
      'active-false': { paramName: 'active', paramValue: false },
    }

    const isBulkDeleteDialogVisible = ref(false)
    const isBulkDeleting = ref(false)
    const dataTable = ref(null)
    const tableScrollbar = ref(null)
    const tableScrollWidth = ref(1250)
    let tableWrapper = null
    let isSyncingScroll = false

    const { hasRole } = useAuth()
    const isAdmin = computed(() => hasRole(['ADMIN']))

    const tableHeaders = computed(() => {
      const headers = (isAdmin.value ? [...dtHeaders.value] : dtHeaders.value.filter(h => h.value !== 'creator' && h.value !== 'academy'))
        .map(h => {
          const widths = {
            name: 180,
            type: 130,
            thumbnail: 110,
            thumbnailFemale: 110,
            video: 80,
            creator: 110,
            academy: 110,
            active: 90,
          }

          return widths[h.value] ? { ...h, width: widths[h.value] } : h
        })

      if (headers.length > 0 && !headers.some(h => h.value === 'assessmentEnabled')) {
        const activeIndex = headers.findIndex(h => h.value === 'active')
        const insertIndex = activeIndex !== -1 ? activeIndex : headers.length

        headers.splice(insertIndex, 0, {
          text: 'ASSESSMENT ENABLED',
          value: 'assessmentEnabled',
          sortable: true,
          width: 110,
        }, {
          text: 'PRESENT IN FLOW SECTION IN THE APP',
          value: 'presentInExercises',
          sortable: true,
          width: 130,
        })
      }

      return headers.map(h => (
        ['assessmentEnabled', 'presentInExercises'].includes(h.value)
          ? { ...h, class: 'compact-table-column', cellClass: 'compact-table-column' }
          : h
      ))
    })

    const isAssessmentEnabled = item => Boolean(item.goal || item.questionnaire?.title)

    const ensureExerciseProperties = data => data.map(ex => ({
      ...ex,
      assessmentEnabled: Boolean(ex.goal || ex.questionnaire?.title),
      presentInExercises: Boolean(ex.presentInExercises),
      video: ex.video || { url: '' },
      videoFemale: ex.videoFemale || { url: '' },
      questionnaire: ex.questionnaire || null,
    }))

    const getTableWrapper = () => {
      tableWrapper = dataTable.value?.$el?.querySelector('.v-data-table__wrapper') || null

      return tableWrapper
    }

    async function syncTableScrollbarWidth() {
      await nextTick()
      const wrapper = getTableWrapper()
      const table = wrapper?.querySelector('table')
      const scrollableWidth = Math.max((table?.scrollWidth || wrapper?.scrollWidth || 1250) - (wrapper?.clientWidth || 0), 0)
      tableScrollWidth.value = window.innerWidth + scrollableWidth
    }

    const fetchAndProcessExercises = async (category = null) => {
      await fetchExercises(category)
      exercises.value = ensureExerciseProperties(exercises.value)
      await syncTableScrollbarWidth()
    }

    const onTableWrapperScroll = event => {
      if (isSyncingScroll || !tableScrollbar.value) return
      isSyncingScroll = true
      tableScrollbar.value.scrollLeft = event.target.scrollLeft
      requestAnimationFrame(() => { isSyncingScroll = false })
    }

    const onTableScrollbarScroll = event => {
      const wrapper = getTableWrapper()
      if (isSyncingScroll || !wrapper) return
      isSyncingScroll = true
      wrapper.scrollLeft = event.target.scrollLeft
      requestAnimationFrame(() => { isSyncingScroll = false })
    }

    onMounted(async () => {
      await fetchAndProcessExercises(ExerciseCategory.FLOW)
      getTableWrapper()?.addEventListener('scroll', onTableWrapperScroll)
      window.addEventListener('resize', syncTableScrollbarWidth)
    })

    onBeforeUnmount(() => {
      getTableWrapper()?.removeEventListener('scroll', onTableWrapperScroll)
      window.removeEventListener('resize', syncTableScrollbarWidth)
    })

    const showDeleteConfirmationDialog = exerciseItem => { exercise.value = exerciseItem; isDeleteDialogVisible.value = true }
    const onDeleteCanceled = () => { exercise.value = structuredClone(blankExercise); isDeleteDialogVisible.value = false }
    const onDeleteConfirmed = async exerciseItem => { await deleteExercise(exerciseItem); await fetchAndProcessExercises(ExerciseCategory.FLOW); isDeleteDialogVisible.value = false }

    const showEditDialog = exerciseItem => {
      const item = structuredClone(exerciseItem)
      if (!item.uniqueKey) item.uniqueKey = uniqueKey()
      if (!item.id) item.category = ExerciseCategory.FLOW
      exercise.value = item
      isEditDialogVisible.value = true
    }

    const onEditCanceled = () => { exercise.value = structuredClone(blankExercise); isEditDialogVisible.value = false }
    const onEditSave = async exerciseItem => {
      try {
        if (!exerciseItem.id) {
          await createExercise(exerciseItem)
        } else {
          await updateExercise(exerciseItem)
        }
        await fetchAndProcessExercises(ExerciseCategory.FLOW)
        isEditDialogVisible.value = false
      } catch (error) {
        console.error(error)
      }
    }

    const showBulkStatusDialog = () => { isBulkStatusDialogVisible.value = true }
    const applyContentPackAction = async () => {
      isBulkUpdating.value = true
      try {
        const saved = await saveContentPackAction()
        if (!saved) return
        await fetchAndProcessExercises(ExerciseCategory.FLOW)
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Content pack updated successfully', SnackbarType.SUCCESS)
      } catch (error) {
        console.error(error)
        showSnackbar('Failed to update content pack', SnackbarType.ERROR)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const applyAcademyAction = async () => {
      isBulkUpdating.value = true
      try {
        const saved = await saveAcademyAction()
        if (!saved) return
        await fetchAndProcessExercises(ExerciseCategory.FLOW)
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Academy updated successfully', SnackbarType.SUCCESS)
      } catch (error) {
        showSnackbar('Failed to update academy', SnackbarType.ERROR)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const updateBulkStatus = async () => {
      if (selected.value.length === 0) return
      if (bulkStatusValue.value === 'delete-selected') {
        isBulkStatusDialogVisible.value = false
        isBulkDeleteDialogVisible.value = true

        return
      }
      isBulkUpdating.value = true
      try {
        if (await beginBulkAction(bulkStatusValue.value)) {
          isBulkUpdating.value = false

          return
        }

        const bulkUpdate = bulkStatusConfig[bulkStatusValue.value]
        if (!bulkUpdate) return

        await updateManyExercises('FLOW_EXERCISE', bulkUpdate.paramName, bulkUpdate.paramValue, selected.value.map(item => item.id))
        await fetchAndProcessExercises(ExerciseCategory.FLOW)
        selected.value = []
        isBulkStatusDialogVisible.value = false
        showSnackbar('Items updated successfully', SnackbarType.SUCCESS)
      } catch (error) {
        console.error(error)
        showSnackbar('Error updating items', SnackbarType.ERROR)
      } finally {
        isBulkUpdating.value = false
      }
    }

    const showBulkDeleteDialog = () => { isBulkDeleteDialogVisible.value = true }
    const deleteBulkItems = async () => {
      if (selected.value.length === 0) return
      isBulkDeleting.value = true
      try {
        await Promise.all(selected.value.map(item => deleteExercise(item)))
        await fetchAndProcessExercises(ExerciseCategory.FLOW)
        selected.value = []
        isBulkDeleteDialogVisible.value = false
        showSnackbar('Items deleted successfully', SnackbarType.SUCCESS)
      } catch (error) {
        console.error(error)
        showSnackbar('Error deleting items', SnackbarType.ERROR)
      } finally {
        isBulkDeleting.value = false
      }
    }

    return {
      tableHeaders,
      isAssessmentEnabled,
      dataTable,
      tableScrollbar,
      tableScrollWidth,
      onTableScrollbarScroll,
      dtExpanded,
      dtSearchQuery,
      blankExercise,
      exercises,
      exercise,
      selected,
      isBulkStatusDialogVisible,
      bulkStatusValue,
      bulkStatusOptions,
      isBulkUpdating,
      showBulkStatusDialog,
      isContentPackDialogVisible,
      isContentPackConfirmationDialogVisible,
      isAcademyDialogVisible,
      isAcademyConfirmationDialogVisible,
      selectedContentPackId,
      selectedAcademyId,
      contentPackOptions,
      academyOptions,
      contentPackConfirmationTitle,
      contentPackConfirmationMessage,
      academyDialogTitle,
      academyConfirmationTitle,
      academyConfirmationMessage,
      hideContentPackDialog,
      hideAcademyDialog,
      showContentPackConfirmationDialog,
      showAcademyConfirmationDialog,
      applyContentPackAction,
      applyAcademyAction,
      resetContentPackDialogs,
      updateBulkStatus,
      isBulkDeleteDialogVisible,
      isBulkDeleting,
      showBulkDeleteDialog,
      deleteBulkItems,
      isDeleteDialogVisible,
      showDeleteConfirmationDialog,
      onDeleteCanceled,
      onDeleteConfirmed,
      isEditDialogVisible,
      showEditDialog,
      onEditCanceled,
      onEditSave,
      isLoading,
      icons: {
        mdiDeleteOutline, mdiPencilOutline, mdiGenderFemale, mdiGenderMale, mdiMagnify,
      },
    }
  },
}
</script>

<style scoped>
.exercises-data-table ::v-deep th {
  font-size: 11px !important;
}

.exercise-table-chip {
  font-size: 11px !important;
}

.exercises-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto !important;
  scrollbar-width: none;
}

.exercises-data-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  height: 0;
}

.exercises-data-table ::v-deep table {
  min-width: 1250px;
}

.exercise-table-scrollbar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  height: 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.exercise-table-scrollbar__content {
  height: 1px;
}
</style>
